if (!customElements.get('ls-modular-addons')) {
  customElements.define('ls-modular-addons', class extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      const options = { signal: this.abortController.signal };
      this.addEventListener('input', () => this.updateSummary(), options);
      this.addEventListener('change', () => this.updateSummary(), options);
      this.addEventListener('click', (event) => {
        const button = event.target.closest('button[data-addon-step]');
        if (!button || button.disabled) return;
        const row = button.closest('[data-addon-role]');
        const input = row.querySelector('input');
        const current = Number(input.value) || 0;
        const minimum = Number(row.dataset.ruleMin);
        const step = Number(row.dataset.ruleStep);
        input.value = button.dataset.addonStep === 'plus'
          ? String(current ? current + step : minimum)
          : String(current <= minimum ? 0 : current - step);
        this.updateSummary();
      }, options);
      this.closest('product-info')?.addEventListener('click', (event) => {
        const button = event.target.closest('[data-ls-addon-puff]');
        if (!button || button.disabled || this.dataset.composition !== 'true') return;
        const row = this.querySelector('[data-addon-role="puff"]');
        const input = row?.querySelector('input');
        if (!input || input.disabled) return;
        input.value = button.dataset.lsAddonPuff === 'true' ? String(Math.max(1, Number(row.dataset.ruleMin))) : '0';
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }, options);
      this.enableControls();
      this.updateSummary();
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    enableControls() {
      this.querySelectorAll('[data-addon-role]').forEach((row) => {
        row.querySelector('input').disabled = row.dataset.available !== 'true';
      });
    }

    refreshFrom(source) {
      if (!source) return;
      const quantities = new Map(Array.from(this.querySelectorAll('[data-addon-role]'))
        .map((row) => [row.dataset.addonRole, row.querySelector('input').value]));
      const wasOpen = this.querySelector('details').open;
      Object.assign(this.dataset, source.dataset);
      this.hidden = source.hidden;
      this.replaceChildren(...Array.from(source.childNodes, (node) => node.cloneNode(true)));
      this.querySelector('details').open = wasOpen;
      this.querySelectorAll('[data-addon-role]').forEach((row) => {
        row.querySelector('input').value = this.dataset.composition === 'true' ? quantities.get(row.dataset.addonRole) || '0' : '0';
      });
      this.enableControls();
      this.updateSummary();
    }

    syncPuffControls() {
      const root = this.closest('product-info');
      const controls = root?.querySelector('[data-ls-independent-puff]');
      if (!controls) return;
      const row = this.querySelector('[data-addon-role="puff"]');
      const input = row?.querySelector('input');
      const available = this.dataset.composition === 'true' && row?.dataset.available === 'true'
        && !!row.dataset.variantId && !input.disabled
        && (input.max === '' || Number(input.max) >= Number(row.dataset.ruleMin));
      const selected = available && Number(input.value) > 0;
      root.querySelectorAll('[data-ls-puff-guidance]').forEach(node => { node.hidden = !selected; });
      controls.querySelectorAll('button').forEach(button => {
        button.disabled = !available;
        button.setAttribute('aria-pressed', String(selected === (button.dataset.lsAddonPuff === 'true')));
      });
      const note = root.querySelector('[data-ls-puff-price]');
      if (note) note.textContent = row?.querySelector('.ls-modular-addons__copy span')?.textContent.trim() || '';
      const plan = root.querySelector('[data-ls-independent-puff-plan]');
      if (plan) plan.style.display = selected ? '' : 'none';
    }

    updateSummary() {
      this.syncPuffControls();
      let count = 0;
      let total = 0;
      this.querySelectorAll('[data-addon-role]').forEach((row) => {
        const input = row.querySelector('input');
        const quantity = Number(input.value) || 0;
        count += quantity;
        total += quantity * Number(row.dataset.price);
        row.querySelector('[data-addon-step="minus"]').disabled = input.disabled || quantity <= 0;
        row.querySelector('[data-addon-step="plus"]').disabled = input.disabled || (input.max !== '' && quantity >= Number(input.max));
      });
      const summary = this.querySelector('.ls-modular-addons__total');
      summary.hidden = count <= 0 || this.dataset.composition !== 'true';
      const format = (amount) => new Intl.NumberFormat(document.documentElement.lang || 'es', {
        style: 'currency', currency: this.dataset.currency || 'EUR'
      }).format(amount / 100);
      this.querySelector('[data-addon-summary]').textContent = this.dataset.selectedSummary
        .replace('[count]', count).replace('[price]', format(total));
    }

    buildAddRequest(formData, request) {
      if (this.dataset.composition !== 'true') return request;
      const items = [];
      for (const row of this.querySelectorAll('[data-addon-role]')) {
        const input = row.querySelector('input');
        const quantity = input.value === '' ? 0 : Number(input.value);
        const minimum = Number(row.dataset.ruleMin);
        const step = Number(row.dataset.ruleStep);
        const valid = Number.isSafeInteger(quantity) && quantity >= 0
          && (quantity === 0 || (quantity >= minimum && (quantity - minimum) % step === 0))
          && (input.max === '' || quantity <= Number(input.max))
          && (quantity === 0 || (row.dataset.available === 'true' && row.dataset.variantId));
        if (!valid) {
          this.querySelector('details').open = true;
          input.focus();
          const error = new Error('Invalid additional piece quantity');
          error.publicMessage = this.dataset.quantityError;
          throw error;
        }
        if (quantity) items.push({ id: row.dataset.variantId, quantity });
      }
      if (!items.length) return request;
      if (!request) {
        const sofa = { id: formData.get('id'), quantity: Number(formData.get('quantity')) || 1 };
        const properties = {};
        for (const [name, value] of formData.entries()) {
          const property = name.match(/^properties\[(.+)\]$/);
          if (property) {
            if (typeof value !== 'string') throw new Error('File properties require multipart');
            properties[property[1]] = value;
          }
        }
        if (Object.keys(properties).length) sofa.properties = properties;
        if (formData.get('selling_plan')) sofa.selling_plan = formData.get('selling_plan');
        request = { items: [sofa] };
        if (formData.get('sections')) request.sections = formData.get('sections');
        if (formData.get('sections_url')) request.sections_url = formData.get('sections_url');
      }
      return { ...request, items: [...request.items, ...items] };
    }
  });
}


